import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { AppLauncher } from '@capacitor/app-launcher';

const NATIVE_MAP_APP_URL_PATTERN = /^(yandexmaps|comgooglemaps|dgis):/i;

function normalizeUrl(url: string): string | null {
  const trimmed = url?.trim();
  return trimmed || null;
}

export function isNativeMapAppUrl(url: string): boolean {
  const normalized = normalizeUrl(url);
  return normalized ? NATIVE_MAP_APP_URL_PATTERN.test(normalized) : false;
}

/** Checks whether the URL can be opened on the current platform. */
export async function canOpenExternalUrl(url: string): Promise<boolean> {
  const normalized = normalizeUrl(url);
  if (!normalized) {
    return false;
  }

  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  if (!isNativeMapAppUrl(normalized)) {
    return true;
  }

  try {
    const { value } = await AppLauncher.canOpenUrl({ url: normalized });
    return value;
  } catch {
    return false;
  }
}

/** Opens URL in a native map app, in-app/system browser, or a new tab on web. */
export async function openExternalUrl(url: string): Promise<void> {
  const normalized = normalizeUrl(url);
  if (!normalized) {
    return;
  }

  try {
    if (Capacitor.isNativePlatform()) {
      if (isNativeMapAppUrl(normalized)) {
        const { completed } = await AppLauncher.openUrl({ url: normalized });
        if (completed) {
          return;
        }
      }

      await Browser.open({ url: normalized });
      return;
    }

    window.open(normalized, '_blank', 'noopener,noreferrer');
  } catch {
    window.open(normalized, '_blank', 'noopener,noreferrer');
  }
}
